import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth, useCourses, useModal } from 'contexts';
import { actionLog, getQuestionsQuiz, registerFolderLog } from 'services';
import { DetailContentInterface } from 'types';
import { Timer } from 'components';
import { shuffleArray } from 'utils';

import './style.scss';
import { FaChevronRight } from 'react-icons/fa';

type QuizProps = {
  content: DetailContentInterface;
  className?: string;
  [key: string]: any;
};

type QuizChoice = { _id?: string; answer: string; label: string; grade: number };

function getTrueFalseChoices(correctAnswer: boolean): QuizChoice[] {
  return [
    { answer: '1', label: 'Verdadeiro', grade: correctAnswer === true ? 1 : 0 },
    { answer: '2', label: 'Falso', grade: correctAnswer === false ? 1 : 0 },
  ];
}

export function Quiz({ content, className, ...rest }: QuizProps) {
  const { getFullProgress } = useCourses();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [questionList, setQuestionList] = useState<any[]>();
  const [step, setStep] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState<any>();
  const [currentQuestionAnswered, setCurrentQuestionAnswered] = useState<any>();
  const [score, setScore] = useState(0);
  const questionRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  async function handleAnswer(item: any) {
    if (!user || currentQuestionAnswered) return;

    if (item.grade === 1) {
      setScore(score + 1);
    }
    setCurrentQuestionAnswered(item.answer);
  }

  function handleNextQuestion() {
    if (!questionList) return;

    setCurrentQuestionAnswered(undefined);

    if (questionIndex + 1 >= questionList?.length) {
      setStep(2);
      submitResult();
    } else {
      setQuestionIndex(questionIndex + 1);
    }
  }

  const submitResult = useCallback(() => {
    // await actionLog('quiz', { score: score * 10 });

    const totalQuestions = questionList?.length || 1;
    const quizHits = score / totalQuestions;

    registerFolderLog(user?._id, content._id, 'done', { quiz_hits: quizHits }).finally(() => {
      getFullProgress();
    });
  }, [content._id, getFullProgress, user?._id, score, questionList?.length]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const questions = await getQuestionsQuiz(content.content);

      const shuffledQuestions = questions.map((item: any) => {
        return {
          ...item,
          choices: shuffleArray(item.choices),
        };
      });

      setQuestionList(shuffledQuestions);
    }

    if (step === 1 && !questionList && !!content) {
      loadData();
    }
  }, [content, questionList, step, submitResult]);

  function getChoiceStatusText(item: QuizChoice) {
    if (!currentQuestionAnswered) return '';
    if (item.grade === 1) return ' — resposta correta';
    if (currentQuestionAnswered === item.answer) return ' — sua resposta, incorreta';
    return '';
  }

  function getFeedbackText() {
    if (!currentQuestionAnswered || !currentQuestion) return '';
    const selected = getChoices().find((item) => item.answer === currentQuestionAnswered);
    return selected?.grade === 1 ? 'Resposta correta!' : 'Resposta incorreta.';
  }

  function getChoices(): QuizChoice[] {
    if (currentQuestion?.type === 'MULTIPLE_CHOICE') return currentQuestion.choices;
    if (currentQuestion?.type === 'TRUE_FALSE') return getTrueFalseChoices(currentQuestion.correctAnswer);
    return [];
  }

  function getAnswerClassName(item: any) {
    const className = 'bg-white text-orange-600';
    const correctClassName = 'text-white bg-green-600';
    const incorrectClassName = 'text-white bg-red-600';

    if (currentQuestionAnswered) {
      if (item.grade === 1) {
        return correctClassName;
      } else if (currentQuestionAnswered === item.answer) {
        return incorrectClassName;
      }
      return className;
    } else {
      return `cursor-pointer ${className}`;
    }
  }

  useEffect(() => {
    try {
      setLoading(false);
      if (questionList && questionList.length > 0) {
        setCurrentQuestion(questionList[questionIndex]);
      }
    } catch (e) {}
  }, [questionList, questionIndex]);

  useEffect(() => {
    if (step === 1 && currentQuestion) questionRef.current?.focus();
  }, [step, currentQuestion]);

  useEffect(() => {
    if (step === 2) resultRef.current?.focus();
  }, [step]);

  // TO DO: Error Boundary
  if (!content) return null;
  // throw new Error("'content' is required attribute.");

  return (
    <div className={`quiz-component flex-1 w-full relative ${className || ''}`}>
      <div className="flex flex-col relative w-full h-full">
        <div className="w-full h-full relative overflow-hidden p-4 lg:p-10">
          <div className="min-h relative h-full text-center flex flex-col justify-center items-center">
            <>
              {!step && (
                <div className="text-white bg-opacity-80 p-0 lg:p-10 rounded-xl">
                  <h1 className="text-xl lg:text-3xl mb-3 font-bold">{content.title}</h1>
                  <button
                    className="bg-orange py-2 lg:py-3 px-3 font-bold text-sm lg:text-lg rounded-full"
                    type="button"
                    onClick={() => setStep(1)}
                  >
                    INICIAR QUIZ
                  </button>
                </div>
              )}

              {!!loading && (
                <div role="status" className="text-white bg-opacity-80 p-0 lg:p-10 rounded-xl">
                  <h1 className="text-lg lg:text-xl">Carregando...</h1>
                </div>
              )}

              {!loading && currentQuestion && step === 1 && (
                <div className="bg-opacity-90 p-0 lg:p-10 rounded-xl min-w-2xl max-w-2xl">
                  <div
                    ref={questionRef}
                    tabIndex={-1}
                    role="group"
                    aria-labelledby="quiz-progress quiz-question"
                    className="focus:outline-none"
                  >
                    <div className="flex flex-row items-center justify-center bg-white rounded-full text-center shadow-xl mb-3 p-2">
                      <h1 id="quiz-progress" className="text-base lg:text-xl text-orange-500 font-bold">
                        Questão {questionIndex + 1} de {questionList?.length}
                      </h1>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-xl text-black">
                      <p
                        id="quiz-question"
                        className="text-base lg:text-xl"
                        dangerouslySetInnerHTML={{ __html: currentQuestion.question }}
                      />
                    </div>
                  </div>
                  <div id="quiz-choices-label" className="py-2 font-bold text-white text-sm lg:text-lg">
                    Escolha a sua resposta:
                  </div>
                  <div role="group" aria-labelledby="quiz-choices-label" className="space-y-2">
                    {getChoices().map((item) => (
                      <button
                        type="button"
                        key={item._id || item.answer}
                        onClick={() => handleAnswer(item)}
                        aria-disabled={!!currentQuestionAnswered}
                        className={`block w-full text-left p-2 ${getAnswerClassName(item)} border rounded-xl text-base lg:text-lg focus:outline-none focus:ring-4 focus:ring-orange-300`}
                      >
                        {item.label}
                        <span className="sr-only">{getChoiceStatusText(item)}</span>
                      </button>
                    ))}
                  </div>

                  <div role="status" aria-live="polite" className="sr-only">
                    {getFeedbackText()}
                  </div>

                  {currentQuestionAnswered && (
                    <div className="mt-4">
                      <button
                        onClick={handleNextQuestion}
                        className="mx-auto p-3 rounded flex text-xl text-white items-center justify-center border focus:outline-none focus:ring-4 focus:ring-orange-300"
                      >
                        {questionIndex + 1 >= (questionList?.length || 0) ? 'Ver resultado' : 'Próxima questão'}{' '}
                        <FaChevronRight aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div
                  ref={resultRef}
                  tabIndex={-1}
                  role="status"
                  className="text-white  bg-opacity-80 p-10 rounded-xl space-y-3 focus:outline-none"
                >
                  <h1 className="text-3xl">Game Over!</h1>
                  <div className="leading-none py-3">
                    <div className="text-xl text-orange-500 ">Respostas corretas:</div>
                    <div className="text-2xl font-bold ">
                      {score} de {questionList?.length}
                    </div>
                  </div>
                </div>
              )}
            </>
          </div>
        </div>
      </div>
    </div>
  );
}
